import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Plus, Trash2, Power, PowerOff, LayoutDashboard, Pencil } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { api } from '@/lib/api'
import { BarChart, type BarDatum } from '@/components/BarChart'
import { ErrorState, Skeleton } from '@/components/States'
import { CsvButton } from '@/components/CsvButton'
import { RefreshButton } from '@/components/RefreshButton'
import { downloadCsv } from '@/lib/csv'
import { useToast } from '@/providers/UiProviders'
import {
  ALERTS_DASH_STR as A,
  createAlertRule,
  createDashboard,
  deleteAlertRule,
  deleteDashboard,
  fetchAlertRules,
  fetchDashboards,
  parseLayout,
  updateAlertRule,
  updateDashboard,
  type AlertChannel,
  type AlertRule,
  type Dashboard,
} from './alertsDashApi'

// A16 — event/observability blokidagi analitika ekranlari. Avval «halol skelet»
// edi (ClickHouse/event-ETL kutardi). MyGram'da event-ETL yo'q, LEKIN `updates`
// jadvali real per-user event oqimi (type/payload/created_at) — event
// kashfiyoti/lug'ati/yo'li shundan jonli hisoblanadi; kohort/prognoz/voronka
// users+updates+messages'dan; SLO job_runs/call_sessions/support/incidents'dan.
// HAMMA raqam jonli Postgres-so'rov — soxta ma'lumot yo'q. Aniq event-ETL yetmagan
// joyda «Postgres-taxminiy» deb belgilanadi. FAQAT A2.10/A2.11 (ogohlantirish/
// dashboard-konstruktor) haqiqiy skelet bo'lib qoladi — ular alohida infra kutadi.
// Modul-lokal (adapters.ts / i18n HOT emas): real-ekran matnlari shu faylda.

const STR = {
  noData: "Ma'lumot yo'q",
  values: 'Qiymatlar',
  // A16.17
  eventDiscovery: 'Event kashfiyoti',
  eventDiscoveryNote: 'updates.type real event oqimi — turlar va hajm (Postgres)',
  event: 'Event',
  count: 'Soni',
  distinct: 'Turlar',
  total: 'Jami',
  top: 'Eng ko‘p',
  // A16.18
  eventTaxonomy: 'Event lug‘ati',
  eventTaxonomyNote: 'Real event turlari + kategoriya + birinchi ko‘rinish + xossalar',
  category: 'Kategoriya',
  firstSeen: 'Birinchi',
  props: 'Xossalar',
  categories: 'Kategoriyalar',
  // A16.19
  userPath: 'Foydalanuvchi yo‘li',
  userPathNote: 'Event-chuqurlik yo‘li — har bosqichdagi modal event (Postgres)',
  step: 'Bosqich',
  users: 'Foydalanuvchi',
  // A16.16
  funnelCompare: 'Voronka taqqoslash',
  funnelCompareNote: 'Bu hafta vs o‘tgan hafta signup kohorti (users/messages/updates)',
  thisWeek: 'Bu hafta',
  lastWeek: 'O‘tgan hafta',
  // A16.20
  segmentCompare: 'Segment taqqoslash',
  segmentCompareNote: 'Top davlatlar × {foydalanuvchi, faol_7k, xabar} (Postgres)',
  segment: 'Segment',
  // A16.21
  cohortBuilder: 'Kohorta konstruktori',
  cohortNote: 'Haftalik signup kohorti × ushlab qolish (users.created_at + updates)',
  cohort: 'Kohort',
  week: 'H',
  // A16.22
  forecasting: 'Prognozlash',
  forecastNote: 'Kunlik registratsiya — 7-kunlik MA + tekis proyeksiya (Postgres)',
  date: 'Sana',
  actual: 'Haqiqiy',
  predicted: 'Bashorat',
  // A16.39
  serviceQuality: 'Xizmat sifati (SLO/SLI)',
  serviceQualityNote: 'Operatsion jadvallardan xizmat-daraja indikatorlari (job/call/tiket/insident)',
  target: 'Maqsad',
  openIncidents: 'Ochiq insidentlar',
  critical: 'Kritik (sev1/2)',
  jobRuns: 'Fon vazifalari (7k)',
  calls: 'Qo‘ng‘iroqlar (7k)',
  ok: 'Yaxshi',
  breach: 'Buzilgan',
  // A16.40
  reportBuilder: 'Hisobot konstruktori',
  reportNote: 'Jonli Postgres metrikalari — tanlab hisobot yig‘ing va CSV eksport qiling',
  metric: 'Metrika',
  value: 'Qiymat',
  selectAll: 'Hammasi',
  clear: 'Tozalash',
  buildReport: 'Tanlangan hisobot',
  selected: 'tanlandi',
} as const

// ---------- shared local presentational bits ----------

function useLoad<T>(fetcher: (s?: AbortSignal) => Promise<T>, reloadKey: number) {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetcher(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [fetcher, reloadKey])
  return { data, loading, error }
}

function KPI({ label, value, accent }: { label: string; value: ReactNode; accent?: boolean }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="text-xs text-tertiary">{label}</div>
      <div className={`mt-0.5 font-mono text-2xl tabular ${accent ? 'text-accent' : 'text-primary'}`}>{value}</div>
    </div>
  )
}

function Panel({ title, children, className = '' }: { title: string; children: ReactNode; className?: string }) {
  return (
    <div className={`max-w-3xl rounded-lg border border-border-default bg-surface-2 p-3 ${className}`}>
      <div className="mb-2 text-sm font-medium text-secondary">{title}</div>
      {children}
    </div>
  )
}

function FunnelBars({ steps }: { steps: { label: string; value: number; sub?: string }[] }) {
  const max = Math.max(1, ...steps.map((s) => s.value))
  return (
    <div className="space-y-1.5">
      {steps.map((s, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-44 shrink-0 truncate text-sm text-secondary" title={s.label}>
            {s.label}
          </div>
          <div className="relative h-7 flex-1 overflow-hidden rounded bg-surface-3">
            <div className="h-full rounded bg-[var(--chart-1)]" style={{ width: `${(s.value / max) * 100}%`, transition: 'width 200ms' }} />
            <div className="absolute inset-0 flex items-center gap-2 px-2 font-mono text-xs tabular text-primary">
              <span>{s.value.toLocaleString()}</span>
              {s.sub && <span className="text-tertiary">· {s.sub}</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function SimpleTable({ head, rows }: { head: string[]; rows: (string | number)[][] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            {head.map((h, i) => (
              <th key={i} className={`border-b border-border-default px-2 py-1.5 font-medium text-tertiary ${i === 0 ? 'text-left' : 'text-right'}`}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>
              {r.map((c, ci) => (
                <td key={ci} className={`border-b border-border-subtle px-2 py-1.5 ${ci === 0 ? 'text-left text-secondary' : 'text-right font-mono tabular text-primary'}`}>
                  {typeof c === 'number' ? c.toLocaleString() : c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ScreenScaffold({
  title,
  id,
  note,
  onReload,
  onExport,
  children,
}: {
  title: string
  id: string
  note: string
  onReload: () => void
  onExport?: () => void
  children: ReactNode
}) {
  return (
    <>
      <PageHeader
        title={title}
        screenId={id}
        description={note}
        actions={
          <div className="flex items-center gap-2">
            <RefreshButton onClick={onReload} />
            {onExport && <CsvButton onClick={onExport} />}
          </div>
        }
      />
      <div className="min-h-0 flex-1 space-y-3 overflow-auto p-3">{children}</div>
    </>
  )
}

// ---------- fetchers (typed to Go analytics_ext handlers) ----------

interface EventRow {
  event: string
  count: number
}
const fetchEventDiscovery = (s?: AbortSignal) =>
  api.get<{ events: EventRow[] }>('/admin/analytics/events/discovery', s ? { signal: s } : undefined).then((d) => d.events)

interface EventTaxonomyRow {
  event: string
  category: string
  firstSeen: number
  props: number
}
const fetchEventTaxonomy = (s?: AbortSignal) =>
  api.get<{ taxonomy: EventTaxonomyRow[] }>('/admin/analytics/events/taxonomy', s ? { signal: s } : undefined).then((d) => d.taxonomy)

interface UserPathStep {
  step: string
  users: number
  dropoff: number
}
const fetchUserPath = (s?: AbortSignal) =>
  api.get<{ path: UserPathStep[] }>('/admin/analytics/events/user-path', s ? { signal: s } : undefined).then((d) => d.path)

interface FunnelStep {
  step: string
  users: number
}
const fetchFunnelCompare = (s?: AbortSignal) =>
  api.get<{ a: FunnelStep[]; b: FunnelStep[] }>('/admin/analytics/funnels/compare', s ? { signal: s } : undefined)

interface SegmentMetric {
  segment: string
  metric: string
  value: number
}
const fetchSegmentCompare = (s?: AbortSignal) =>
  api.get<{ segments: SegmentMetric[] }>('/admin/analytics/segments/compare', s ? { signal: s } : undefined).then((d) => d.segments)

interface CohortCell {
  cohort: string
  period: number
  retention: number
}
const fetchCohorts = (s?: AbortSignal) =>
  api.get<{ cohort: CohortCell[] }>('/admin/analytics/cohorts', s ? { signal: s } : undefined).then((d) => d.cohort)

interface ForecastPoint {
  date: string
  predicted: number
  actual: number | null
}
const fetchForecast = (s?: AbortSignal) =>
  api.get<{ forecast: ForecastPoint[] }>('/admin/analytics/forecast', s ? { signal: s } : undefined).then((d) => d.forecast)

interface SLO {
  key: string
  label: string
  value: number
  target: number
  ok: boolean
}
interface ServiceQualityReport {
  slos: SLO[]
  jobRuns7d: number
  calls7d: number
  tickets30d: number
  openIncidents: number
  criticalIncidents: number
}
const fetchServiceQuality = (s?: AbortSignal) =>
  api.get<ServiceQualityReport>('/admin/analytics/service-quality', s ? { signal: s } : undefined)

interface ReportMetric {
  key: string
  label: string
  value: number
  group: string
}
const fetchReportMetrics = (s?: AbortSignal) =>
  api.get<{ metrics: ReportMetric[] }>('/admin/analytics/report', s ? { signal: s } : undefined).then((d) => d.metrics)

// ---------- A16.17 Event discovery ----------

export function EventDiscovery() {
  const [reload, setReload] = useState(0)
  const { data, loading, error } = useLoad(fetchEventDiscovery, reload)
  const bars = useMemo<BarDatum[]>(() => (data ?? []).map((r) => ({ label: r.event, value: r.count })), [data])
  const total = (data ?? []).reduce((a, r) => a + r.count, 0)
  const onExport = () => data && downloadCsv('event-discovery', [[STR.event, STR.count], ...data.map((r) => [r.event, r.count])])
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <ScreenScaffold title={STR.eventDiscovery} id="A16.17" note={STR.eventDiscoveryNote} onReload={() => setReload((n) => n + 1)} onExport={data ? onExport : undefined}>
      <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-3">
        <KPI label={STR.distinct} value={data?.length ?? '—'} accent />
        <KPI label={STR.total} value={loading ? '—' : total.toLocaleString()} />
        <KPI label={STR.top} value={<span className="text-lg">{data?.[0]?.event ?? '—'}</span>} />
      </div>
      <Panel title={STR.count}>
        {loading ? <Skeleton className="h-40 w-full" /> : <BarChart data={bars} emptyLabel={STR.noData} tableLabel={STR.values} />}
      </Panel>
      <Panel title={STR.event}>
        {loading ? <Skeleton className="h-40 w-full" /> : <SimpleTable head={[STR.event, STR.count]} rows={(data ?? []).map((r) => [r.event, r.count])} />}
      </Panel>
    </ScreenScaffold>
  )
}

// ---------- A16.18 Event taxonomy ----------

export function EventTaxonomy() {
  const [reload, setReload] = useState(0)
  const { data, loading, error } = useLoad(fetchEventTaxonomy, reload)
  const cats = new Set((data ?? []).map((r) => r.category)).size
  const onExport = () =>
    data && downloadCsv('event-taxonomy', [[STR.event, STR.category, STR.firstSeen, STR.props], ...data.map((r) => [r.event, r.category, new Date(r.firstSeen).toISOString().slice(0, 10), r.props])])
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <ScreenScaffold title={STR.eventTaxonomy} id="A16.18" note={STR.eventTaxonomyNote} onReload={() => setReload((n) => n + 1)} onExport={data ? onExport : undefined}>
      <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-3">
        <KPI label={STR.distinct} value={data?.length ?? '—'} accent />
        <KPI label={STR.categories} value={loading ? '—' : cats} />
      </div>
      <Panel title={STR.eventTaxonomy}>
        {loading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <SimpleTable
            head={[STR.event, STR.category, STR.firstSeen, STR.props]}
            rows={(data ?? []).map((r) => [r.event, r.category, r.firstSeen ? new Date(r.firstSeen).toISOString().slice(0, 10) : '—', r.props])}
          />
        )}
      </Panel>
    </ScreenScaffold>
  )
}

// ---------- A16.19 User path ----------

export function UserPath() {
  const [reload, setReload] = useState(0)
  const { data, loading, error } = useLoad(fetchUserPath, reload)
  const onExport = () => data && downloadCsv('user-path', [[STR.step, STR.users, 'dropoff'], ...data.map((r) => [r.step, r.users, `${r.dropoff}%`])])
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <ScreenScaffold title={STR.userPath} id="A16.19" note={STR.userPathNote} onReload={() => setReload((n) => n + 1)} onExport={data ? onExport : undefined}>
      <Panel title={STR.userPath}>
        {loading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <FunnelBars steps={(data ?? []).map((r) => ({ label: r.step, value: r.users, sub: r.dropoff > 0 ? `−${r.dropoff}%` : undefined }))} />
        )}
      </Panel>
    </ScreenScaffold>
  )
}

// ---------- A16.16 Funnel comparison ----------

export function FunnelComparison() {
  const [reload, setReload] = useState(0)
  const { data, loading, error } = useLoad(fetchFunnelCompare, reload)
  const onExport = () =>
    data &&
    downloadCsv('funnel-compare', [
      [STR.step, STR.thisWeek, STR.lastWeek],
      ...data.a.map((s, i) => [s.step, s.users, data.b[i]?.users ?? 0]),
    ])
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <ScreenScaffold title={STR.funnelCompare} id="A16.16" note={STR.funnelCompareNote} onReload={() => setReload((n) => n + 1)} onExport={data ? onExport : undefined}>
      <div className="grid max-w-3xl gap-3 md:grid-cols-2">
        <Panel title={STR.thisWeek} className="max-w-none">
          {loading ? <Skeleton className="h-40 w-full" /> : <FunnelBars steps={(data?.a ?? []).map((s) => ({ label: s.step, value: s.users }))} />}
        </Panel>
        <Panel title={STR.lastWeek} className="max-w-none">
          {loading ? <Skeleton className="h-40 w-full" /> : <FunnelBars steps={(data?.b ?? []).map((s) => ({ label: s.step, value: s.users }))} />}
        </Panel>
      </div>
    </ScreenScaffold>
  )
}

// ---------- A16.20 Segment comparison ----------

export function SegmentComparison() {
  const [reload, setReload] = useState(0)
  const { data, loading, error } = useLoad(fetchSegmentCompare, reload)
  const { metrics, segments, grid } = useMemo(() => {
    const list = data ?? []
    const metrics = Array.from(new Set(list.map((r) => r.metric)))
    const segments = Array.from(new Set(list.map((r) => r.segment)))
    const grid = new Map<string, number>()
    for (const r of list) grid.set(`${r.segment}|${r.metric}`, r.value)
    return { metrics, segments, grid }
  }, [data])
  const onExport = () =>
    downloadCsv('segment-compare', [[STR.segment, ...metrics], ...segments.map((seg) => [seg, ...metrics.map((m) => grid.get(`${seg}|${m}`) ?? 0)])])
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <ScreenScaffold title={STR.segmentCompare} id="A16.20" note={STR.segmentCompareNote} onReload={() => setReload((n) => n + 1)} onExport={data ? onExport : undefined}>
      <Panel title={STR.segmentCompare}>
        {loading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <SimpleTable head={[STR.segment, ...metrics]} rows={segments.map((seg) => [seg, ...metrics.map((m) => grid.get(`${seg}|${m}`) ?? 0)])} />
        )}
      </Panel>
    </ScreenScaffold>
  )
}

// ---------- A16.21 Cohort builder (retention grid) ----------

export function CohortBuilder() {
  const [reload, setReload] = useState(0)
  const { data, loading, error } = useLoad(fetchCohorts, reload)
  const { cohorts, periods, cell } = useMemo(() => {
    const list = data ?? []
    const cohorts = Array.from(new Set(list.map((c) => c.cohort))).sort()
    const maxP = list.reduce((m, c) => Math.max(m, c.period), 0)
    const periods = Array.from({ length: maxP + 1 }, (_, i) => i)
    const cell = new Map<string, number>()
    for (const c of list) cell.set(`${c.cohort}|${c.period}`, c.retention)
    return { cohorts, periods, cell }
  }, [data])
  const onExport = () =>
    downloadCsv('cohorts', [[STR.cohort, ...periods.map((p) => `${STR.week}${p}`)], ...cohorts.map((co) => [co, ...periods.map((p) => cell.get(`${co}|${p}`) ?? '')])])
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <ScreenScaffold title={STR.cohortBuilder} id="A16.21" note={STR.cohortNote} onReload={() => setReload((n) => n + 1)} onExport={data ? onExport : undefined}>
      <Panel title={STR.cohortBuilder}>
        {loading ? (
          <Skeleton className="h-40 w-full" />
        ) : cohorts.length === 0 ? (
          <p className="py-8 text-center text-sm text-tertiary">{STR.noData}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="border-collapse text-xs">
              <thead>
                <tr>
                  <th className="border-b border-border-default px-2 py-1.5 text-left font-medium text-tertiary">{STR.cohort}</th>
                  {periods.map((p) => (
                    <th key={p} className="border-b border-border-default px-2 py-1.5 text-center font-medium text-tertiary">
                      {STR.week}
                      {p}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cohorts.map((co) => (
                  <tr key={co}>
                    <td className="whitespace-nowrap px-2 py-1 font-mono text-secondary">{co}</td>
                    {periods.map((p) => {
                      const v = cell.get(`${co}|${p}`)
                      return (
                        <td key={p} className="px-1 py-1 text-center">
                          {v === undefined ? (
                            <span className="text-border-default">·</span>
                          ) : (
                            <span
                              className="inline-block min-w-[38px] rounded px-1 py-0.5 font-mono tabular text-primary"
                              style={{ background: 'var(--chart-1)', opacity: 0.18 + 0.82 * (v / 100) }}
                            >
                              {v}%
                            </span>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </ScreenScaffold>
  )
}

// ---------- A16.22 Forecasting ----------

export function Forecasting() {
  const [reload, setReload] = useState(0)
  const { data, loading, error } = useLoad(fetchForecast, reload)
  const bars = useMemo<BarDatum[]>(
    () => (data ?? []).map((p) => ({ label: p.date.slice(5), value: p.actual ?? p.predicted, title: p.date })),
    [data],
  )
  const onExport = () => data && downloadCsv('forecast', [[STR.date, STR.actual, STR.predicted], ...data.map((p) => [p.date, p.actual ?? '', p.predicted])])
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <ScreenScaffold title={STR.forecasting} id="A16.22" note={STR.forecastNote} onReload={() => setReload((n) => n + 1)} onExport={data ? onExport : undefined}>
      <Panel title={STR.predicted}>
        {loading ? <Skeleton className="h-40 w-full" /> : <BarChart data={bars} emptyLabel={STR.noData} tableLabel={STR.values} />}
      </Panel>
      <Panel title={STR.forecasting}>
        {loading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <SimpleTable head={[STR.date, STR.actual, STR.predicted]} rows={(data ?? []).map((p) => [p.date, p.actual ?? '—', p.predicted])} />
        )}
      </Panel>
    </ScreenScaffold>
  )
}

// ---------- A16.39 Service quality (SLO/SLI) ----------

export function ServiceQuality() {
  const [reload, setReload] = useState(0)
  const { data, loading, error } = useLoad(fetchServiceQuality, reload)
  const onExport = () =>
    data && downloadCsv('service-quality', [[STR.metric, STR.value, STR.target, 'ok'], ...data.slos.map((s) => [s.label, `${s.value}%`, `${s.target}%`, s.ok ? '1' : '0'])])
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <ScreenScaffold title={STR.serviceQuality} id="A16.39" note={STR.serviceQualityNote} onReload={() => setReload((n) => n + 1)} onExport={data ? onExport : undefined}>
      <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
        <KPI label={STR.openIncidents} value={loading ? '—' : (data?.openIncidents ?? 0)} accent={!!data && data.openIncidents > 0} />
        <KPI label={STR.critical} value={loading ? '—' : (data?.criticalIncidents ?? 0)} />
        <KPI label={STR.jobRuns} value={loading ? '—' : (data?.jobRuns7d ?? 0)} />
        <KPI label={STR.calls} value={loading ? '—' : (data?.calls7d ?? 0)} />
      </div>
      {loading ? (
        <Skeleton className="h-40 max-w-3xl" />
      ) : (
        <div className="grid max-w-3xl gap-2 md:grid-cols-3">
          {(data?.slos ?? []).map((s) => (
            <div key={s.key} className={`rounded-lg border bg-surface-2 p-3 ${s.ok ? 'border-border-default' : 'border-danger'}`}>
              <div className="text-xs text-tertiary">{s.label}</div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className={`font-mono text-2xl tabular ${s.ok ? 'text-primary' : 'text-danger'}`}>{s.value}%</span>
                <span className="text-xs text-tertiary">
                  {STR.target} {s.target}%
                </span>
              </div>
              <div className={`mt-1 inline-block rounded px-1.5 py-0.5 text-xs ${s.ok ? 'bg-surface-3 text-secondary' : 'bg-danger/10 text-danger'}`}>
                {s.ok ? STR.ok : STR.breach}
              </div>
            </div>
          ))}
        </div>
      )}
    </ScreenScaffold>
  )
}

// ---------- A16.40 Report builder ----------

export function ReportBuilder() {
  const [reload, setReload] = useState(0)
  const { data, loading, error } = useLoad(fetchReportMetrics, reload)
  const [selected, setSelected] = useState<Set<string> | null>(null)
  // Default: all metrics selected once data lands.
  const sel = selected ?? new Set((data ?? []).map((m) => m.key))
  const groups = useMemo(() => {
    const g = new Map<string, ReportMetric[]>()
    for (const m of data ?? []) {
      const arr = g.get(m.group) ?? []
      arr.push(m)
      g.set(m.group, arr)
    }
    return Array.from(g.entries())
  }, [data])
  const toggle = (key: string) => {
    const next = new Set(sel)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    setSelected(next)
  }
  const chosen = (data ?? []).filter((m) => sel.has(m.key))
  const onExport = () => downloadCsv('report', [[STR.metric, STR.value], ...chosen.map((m) => [m.label, m.value])])
  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <ScreenScaffold title={STR.reportBuilder} id="A16.40" note={STR.reportNote} onReload={() => setReload((n) => n + 1)} onExport={data ? onExport : undefined}>
      {loading ? (
        <Skeleton className="h-64 max-w-3xl" />
      ) : (
        <div className="grid max-w-3xl gap-3 md:grid-cols-2">
          <Panel title={STR.metric} className="max-w-none">
            <div className="mb-2 flex gap-2">
              <button
                type="button"
                onClick={() => setSelected(new Set((data ?? []).map((m) => m.key)))}
                className="rounded-md border border-border-default bg-surface-2 px-2 py-0.5 text-xs text-secondary hover:text-primary"
              >
                {STR.selectAll}
              </button>
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                className="rounded-md border border-border-default bg-surface-2 px-2 py-0.5 text-xs text-secondary hover:text-primary"
              >
                {STR.clear}
              </button>
            </div>
            <div className="space-y-3">
              {groups.map(([group, items]) => (
                <div key={group}>
                  <div className="mb-1 text-xs font-medium text-tertiary">{group}</div>
                  <div className="space-y-1">
                    {items.map((m) => (
                      <label key={m.key} className="flex cursor-pointer items-center gap-2 text-sm text-secondary">
                        <input type="checkbox" checked={sel.has(m.key)} onChange={() => toggle(m.key)} className="accent-[var(--chart-1)]" />
                        <span className="flex-1">{m.label}</span>
                        <span className="font-mono tabular text-tertiary">{m.value.toLocaleString()}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Panel>
          <Panel title={`${STR.buildReport} (${chosen.length} ${STR.selected})`} className="max-w-none">
            {chosen.length === 0 ? (
              <p className="py-8 text-center text-sm text-tertiary">{STR.noData}</p>
            ) : (
              <SimpleTable head={[STR.metric, STR.value]} rows={chosen.map((m) => [m.label, m.value])} />
            )}
          </Panel>
        </div>
      )}
    </ScreenScaffold>
  )
}

// ==========================================================================
// A2.10 / A2.11 — JONLI (real). Ogohlantirish qoidalari + dashboard layoutlari
// admin boshqaradigan registrlardan (migration 000250: admin_alert_rules,
// admin_dashboards). Metrika katalogi jonli A16.40 (/admin/analytics/report).
// ==========================================================================

const CHANNELS: { value: AlertChannel; label: string }[] = [
  { value: 'inapp', label: A.chInapp },
  { value: 'email', label: A.chEmail },
  { value: 'telegram', label: A.chTelegram },
  { value: 'webhook', label: A.chWebhook },
]

const inputCls =
  'rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary outline-none focus:border-border-strong'

/** A2.10 — Ogohlantirishlar markazi (real CRUD). */
export function AlertsCenter() {
  const { toast } = useToast()
  const [rules, setRules] = useState<AlertRule[] | null>(null)
  const [metrics, setMetrics] = useState<ReportMetric[]>([])
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  const [busy, setBusy] = useState(false)

  const [name, setName] = useState('')
  const [metric, setMetric] = useState('')
  const [threshold, setThreshold] = useState('')
  const [channel, setChannel] = useState<AlertChannel>('inapp')
  const [enabled, setEnabled] = useState(true)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setError(null)
    Promise.all([fetchAlertRules(ac.signal), fetchReportMetrics(ac.signal)])
      .then(([r, m]) => {
        if (!alive) return
        setRules(r)
        setMetrics(m)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])

  const metricMap = useMemo(() => new Map(metrics.map((m) => [m.key, m])), [metrics])

  const add = async () => {
    if (!name.trim()) return
    setBusy(true)
    try {
      await createAlertRule({
        name: name.trim(),
        metric,
        threshold: Number(threshold) || 0,
        channel,
        enabled,
      })
      toast(A.saved, { tone: 'success' })
      setName('')
      setMetric('')
      setThreshold('')
      setChannel('inapp')
      setEnabled(true)
      setReload((n) => n + 1)
    } catch {
      toast(A.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const toggle = async (r: AlertRule) => {
    try {
      await updateAlertRule(r.id, {
        name: r.name,
        metric: r.metric,
        threshold: r.threshold,
        channel: r.channel,
        enabled: !r.enabled,
      })
      setReload((n) => n + 1)
    } catch {
      toast(A.failed, { tone: 'error' })
    }
  }

  const remove = async (r: AlertRule) => {
    try {
      await deleteAlertRule(r.id)
      toast(A.deleted, { tone: 'success' })
      setReload((n) => n + 1)
    } catch {
      toast(A.failed, { tone: 'error' })
    }
  }

  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />

  return (
    <ScreenScaffold title={A.alertsTitle} id="A2.10" note={A.alertsNote} onReload={() => setReload((n) => n + 1)}>
      <Panel title={A.addRule} className="max-w-none">
        <div className="flex flex-wrap items-center gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={A.ruleName} className={`${inputCls} min-w-[160px] flex-1`} />
          <select value={metric} onChange={(e) => setMetric(e.target.value)} className={inputCls}>
            <option value="">{A.metric}</option>
            {metrics.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
          </select>
          <input
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            placeholder={A.threshold}
            inputMode="decimal"
            className={`${inputCls} w-28`}
          />
          <select value={channel} onChange={(e) => setChannel(e.target.value as AlertChannel)} className={inputCls}>
            {CHANNELS.map((ch) => (
              <option key={ch.value} value={ch.value}>
                {ch.label}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-sm text-secondary">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="accent-[var(--chart-1)]" />
            {A.enabled}
          </label>
          <button
            type="button"
            onClick={add}
            disabled={busy || !name.trim()}
            className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            <Plus size={14} />
            {A.addRule}
          </button>
        </div>
      </Panel>

      <Panel title={A.alertsTitle} className="max-w-none">
        {rules === null ? (
          <Skeleton className="h-40 w-full" />
        ) : rules.length === 0 ? (
          <p className="py-8 text-center text-sm text-tertiary">{A.noRules}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  {[A.ruleName, A.metric, A.threshold, A.current, A.channel, A.enabled, ''].map((h, i) => (
                    <th key={i} className={`border-b border-border-default px-2 py-1.5 font-medium text-tertiary ${i >= 2 && i <= 3 ? 'text-right' : 'text-left'}`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => {
                  const m = metricMap.get(r.metric)
                  return (
                    <tr key={r.id}>
                      <td className="border-b border-border-subtle px-2 py-1.5 text-primary">{r.name}</td>
                      <td className="border-b border-border-subtle px-2 py-1.5 text-secondary">{m?.label ?? r.metric ?? '—'}</td>
                      <td className="border-b border-border-subtle px-2 py-1.5 text-right font-mono tabular text-primary">{r.threshold.toLocaleString()}</td>
                      <td className="border-b border-border-subtle px-2 py-1.5 text-right font-mono tabular text-tertiary">{m ? m.value.toLocaleString() : '—'}</td>
                      <td className="border-b border-border-subtle px-2 py-1.5 text-secondary">{CHANNELS.find((c) => c.value === r.channel)?.label ?? r.channel}</td>
                      <td className="border-b border-border-subtle px-2 py-1.5">
                        <span className={`inline-block rounded px-1.5 py-0.5 text-xs ${r.enabled ? 'bg-success/10 text-success' : 'bg-surface-3 text-tertiary'}`}>
                          {r.enabled ? A.enabled : A.disabled}
                        </span>
                      </td>
                      <td className="border-b border-border-subtle px-2 py-1.5">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => toggle(r)}
                            title={r.enabled ? A.disable : A.enable}
                            className="rounded p-1 text-secondary hover:bg-surface-3 hover:text-primary"
                          >
                            {r.enabled ? <PowerOff size={14} /> : <Power size={14} />}
                          </button>
                          <button
                            type="button"
                            onClick={() => remove(r)}
                            title={A.del}
                            className="rounded p-1 text-danger hover:bg-danger/10"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </ScreenScaffold>
  )
}

/** A2.11 — Dashboard konstruktori (real config CRUD). */
export function DashboardBuilder() {
  const { toast } = useToast()
  const [dashboards, setDashboards] = useState<Dashboard[] | null>(null)
  const [metrics, setMetrics] = useState<ReportMetric[]>([])
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  const [busy, setBusy] = useState(false)

  const [editId, setEditId] = useState<number | null>(null)
  const [name, setName] = useState('')
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [openId, setOpenId] = useState<number | null>(null)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setError(null)
    Promise.all([fetchDashboards(ac.signal), fetchReportMetrics(ac.signal)])
      .then(([d, m]) => {
        if (!alive) return
        setDashboards(d)
        setMetrics(m)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])

  const metricMap = useMemo(() => new Map(metrics.map((m) => [m.key, m])), [metrics])
  const groups = useMemo(() => {
    const g = new Map<string, ReportMetric[]>()
    for (const m of metrics) {
      const arr = g.get(m.group) ?? []
      arr.push(m)
      g.set(m.group, arr)
    }
    return Array.from(g.entries())
  }, [metrics])

  const resetForm = () => {
    setEditId(null)
    setName('')
    setPicked(new Set())
  }

  const toggleMetric = (key: string) => {
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const save = async () => {
    if (!name.trim() || picked.size === 0) {
      toast(A.emptyPick, { tone: 'error' })
      return
    }
    setBusy(true)
    try {
      const keys = Array.from(picked)
      if (editId != null) await updateDashboard(editId, name.trim(), keys)
      else await createDashboard(name.trim(), keys)
      toast(A.saved, { tone: 'success' })
      resetForm()
      setReload((n) => n + 1)
    } catch {
      toast(A.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const edit = (d: Dashboard) => {
    setEditId(d.id)
    setName(d.name)
    setPicked(new Set(parseLayout(d.layout)))
  }

  const remove = async (d: Dashboard) => {
    try {
      await deleteDashboard(d.id)
      toast(A.deleted, { tone: 'success' })
      if (openId === d.id) setOpenId(null)
      if (editId === d.id) resetForm()
      setReload((n) => n + 1)
    } catch {
      toast(A.failed, { tone: 'error' })
    }
  }

  const opened = (dashboards ?? []).find((d) => d.id === openId) ?? null
  const openedMetrics = opened ? parseLayout(opened.layout).map((k) => metricMap.get(k)).filter((m): m is ReportMetric => !!m) : []

  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />

  return (
    <ScreenScaffold title={A.dashTitle} id="A2.11" note={A.dashNote} onReload={() => setReload((n) => n + 1)}>
      <div className="grid max-w-5xl gap-3 md:grid-cols-2">
        <Panel title={editId != null ? `${A.editing}: ${name}` : A.newDashboard} className="max-w-none">
          <div className="space-y-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={A.dashName} className={`${inputCls} w-full`} />
            <div>
              <div className="mb-1 text-xs font-medium text-tertiary">{A.pickMetrics}</div>
              {metrics.length === 0 ? (
                <Skeleton className="h-24 w-full" />
              ) : (
                <div className="max-h-72 space-y-3 overflow-y-auto pr-1">
                  {groups.map(([group, items]) => (
                    <div key={group}>
                      <div className="mb-1 text-xs font-medium text-tertiary">{group}</div>
                      <div className="space-y-1">
                        {items.map((m) => (
                          <label key={m.key} className="flex cursor-pointer items-center gap-2 text-sm text-secondary">
                            <input type="checkbox" checked={picked.has(m.key)} onChange={() => toggleMetric(m.key)} className="accent-[var(--chart-1)]" />
                            <span className="flex-1">{m.label}</span>
                            <span className="font-mono tabular text-tertiary">{m.value.toLocaleString()}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={save}
                disabled={busy || !name.trim() || picked.size === 0}
                className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
              >
                <Plus size={14} />
                {editId != null ? A.update : A.create}
              </button>
              {editId != null && (
                <button type="button" onClick={resetForm} className="rounded-md border border-border-default bg-surface-2 px-3 py-1.5 text-sm text-secondary hover:text-primary">
                  {A.cancel}
                </button>
              )}
            </div>
          </div>
        </Panel>

        <Panel title={A.savedDashboards} className="max-w-none">
          {dashboards === null ? (
            <Skeleton className="h-40 w-full" />
          ) : dashboards.length === 0 ? (
            <p className="py-8 text-center text-sm text-tertiary">{A.noDashboards}</p>
          ) : (
            <div className="space-y-2">
              {dashboards.map((d) => {
                const count = parseLayout(d.layout).length
                return (
                  <div key={d.id} className={`flex items-center gap-2 rounded-lg border p-2 ${openId === d.id ? 'border-accent' : 'border-border-default'} bg-surface-1`}>
                    <LayoutDashboard size={16} className="shrink-0 text-tertiary" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm text-primary">{d.name}</div>
                      <div className="text-xs text-tertiary">
                        {count} {A.widgets}
                      </div>
                    </div>
                    <button type="button" onClick={() => setOpenId(openId === d.id ? null : d.id)} className="rounded-md border border-border-default bg-surface-2 px-2 py-1 text-xs text-secondary hover:text-primary">
                      {A.open}
                    </button>
                    <button type="button" onClick={() => edit(d)} title={A.update} className="rounded p-1 text-secondary hover:bg-surface-3 hover:text-primary">
                      <Pencil size={14} />
                    </button>
                    <button type="button" onClick={() => remove(d)} title={A.del} className="rounded p-1 text-danger hover:bg-danger/10">
                      <Trash2 size={14} />
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </Panel>
      </div>

      {opened && (
        <Panel title={opened.name} className="max-w-none">
          {openedMetrics.length === 0 ? (
            <p className="py-6 text-center text-sm text-tertiary">{A.emptyPick}</p>
          ) : (
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              {openedMetrics.map((m) => (
                <KPI key={m.key} label={m.label} value={m.value.toLocaleString()} />
              ))}
            </div>
          )}
        </Panel>
      )}
    </ScreenScaffold>
  )
}
